/** @jsxImportSource @emotion/react */
import { Header, useSession } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Icon,
  Input,
  Modal,
  Select,
  Typography,
  useTheme,
  ToggleButtonGroup,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import {
  CreateCaseModal,
  QBLoader,
  Transactions,
  KycDocumentImages,
} from 'components';
import { FCUCards } from 'components/FCUCards';
import { KeyDownAutoFocus } from 'components/KeyDownAutoFocus';
import { useSelected, useSetSelected } from 'tabs/AppUsers/context';
import {
  updateUser,
  searchAppUsers,
  getKycDocumentImage,
  getKycDocumentImages,
} from '../../api';
import { AccountStatus, getLabel, VerificationStatus } from '../../types';
import { History } from './components/History';
import { OffboardCustomer } from './components/OffboardCustomer';
import { OnboardingTable } from './components/OnboardingTable';
import { RequestProofOfFunds } from './components/RequestProofOfFunds';
import { RetriggerOnboarding } from './components/ReTriggerOnboarding';

const parseIntOrZeroToString = (value?: string | null) =>
  (parseInt(value ?? '0') || 0).toString();

type Tab = 'details' | 'transactions' | 'history';

export const AppUserDetails = () => {
  const session = useSession();
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const { userId } = useParams();
  const setSelected = useSetSelected();
  const user = useSelected();
  if (!userId) {
    throw new Error('userId is required');
  }

  const [tab, setTab] = useState<Tab>('details');
  const [riskScoreAdjustment, setRiskScoreAdjustment] = useState<string | null>(
    null
  );
  const [status, setStatus] = useState<AccountStatus>(AccountStatus.ACTIVE);
  const [sourceOfFundsVerificationStatus, setSourceOfFundsVerificationStatus] =
    useState<VerificationStatus>();

  const hasEditPermission =
    session.authenticated && session.permissions.includes('edit_qb_users');

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showCustomerInfoModal, setShowCustomerInfoModal] = useState(false);
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);

  const [{ loading: searchLoading }, search] = useAsyncFn(async () => {
    if (userId) {
      const { profiles } = await searchAppUsers({
        filters: { userId },
        pagination: { page: 1, size: 1 },
      });
      if (profiles.length > 0) {
        setSelected(profiles[0]);
      }
    }
  }, [userId]);

  const [{ loading }, doUpdate] = useAsyncFn(async () => {
    if (!user || !user.externalReference) return;

    const response = await updateUser({
      userReference: user.externalReference,
      status,
      sourceOfFundsVerificationStatus,
      riskScoreAdjustment:
        parseIntOrZeroToString(
          user.kyc.riskScoreAdjustment
            ? user.kyc.riskScoreAdjustment.toString()
            : null
        ) !== parseIntOrZeroToString(riskScoreAdjustment)
          ? parseInt(parseIntOrZeroToString(riskScoreAdjustment))
          : undefined,
    });
    setShowUpdateModal(false);
    void search();
    return response;
  }, [status, user, riskScoreAdjustment, sourceOfFundsVerificationStatus]);

  useEffect(() => {
    if (!user || user.userId !== userId) {
      void search();
      return;
    }

    setStatus(user.status);
    setRiskScoreAdjustment(
      parseIntOrZeroToString(
        user.kyc.riskScoreAdjustment
          ? user.kyc.riskScoreAdjustment.toString()
          : null
      )
    );
    setSourceOfFundsVerificationStatus(
      user.kyc.sourceOfFundsVerificationStatus
    );
  }, [user, search, userId]);

  const hasChanges =
    user &&
    (user.status !== status ||
      parseIntOrZeroToString(
        user.kyc.riskScoreAdjustment
          ? user.kyc.riskScoreAdjustment.toString()
          : null
      ) !== parseIntOrZeroToString(riskScoreAdjustment) ||
      user.kyc.sourceOfFundsVerificationStatus !==
        sourceOfFundsVerificationStatus);

  const hasOnboardingHistory =
    user && Object.keys(user.kyc.onboarding).length > 0;

  if (searchLoading) return <QBLoader />;

  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gap: spacing.xxs,
            alignItems: 'center',
          }}
        >
          <div
            css={{ display: 'flex', gap: spacing.xxxs, alignItems: 'center' }}
          >
            <div
              css={{
                cursor: 'pointer',
                ':hover': {
                  textDecoration: 'underline',
                  textDecorationColor: palette.grey.x300,
                },
              }}
              onClick={() => navigate('/users/app')}
            >
              <Typography variant="subtitle" color={palette.grey.x300}>
                App Users
              </Typography>
            </div>
            <div
              css={{
                transform: 'rotate(180deg)',
                svg: {
                  fill: 'none',
                  stroke: palette.grey.x300,
                },
              }}
            >
              <Icon name="chevron" />
            </div>
            <Typography variant="subtitle">{user && user.userId}</Typography>
          </div>
          {hasEditPermission && (
            <UtilityButton
              loading={loading}
              text="Update"
              onClick={() => setShowUpdateModal(true)}
              disabled={!hasChanges}
            />
          )}
        </div>
      </Header>
      <div>
        <div css={{ marginBottom: spacing.xxs }}>
          <ToggleButtonGroup
            onChange={(option) => setTab(option as Tab)}
            selected={tab}
            toggleOptions={[
              { label: 'Details', value: 'details' },
              { label: 'Transactions', value: 'transactions' },
              { label: 'History', value: 'history' },
            ]}
          />
        </div>
        {tab === 'details' && (
          <div css={{ display: 'grid', gap: spacing.xxs }}>
            <Card>
              <div
                css={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: spacing.xxxs,
                }}
              >
                {user && (
                  <>
                    <Select
                      label={getLabel('status')}
                      disabled={!hasEditPermission || loading}
                      options={Object.keys(AccountStatus).map((s) => ({
                        label: s,
                        value: s,
                      }))}
                      values={[status]}
                      onSelect={(s) => setStatus(s[0] as AccountStatus)}
                      onKeyDown={(e) => {
                        if (
                          e.key === 'Enter' &&
                          hasEditPermission &&
                          hasChanges
                        ) {
                          setShowUpdateModal(true);
                        }
                      }}
                    />
                    <Input
                      type="number"
                      label={getLabel('riskScoreAdjustment')}
                      disabled={!hasEditPermission || loading}
                      value={riskScoreAdjustment ?? ''}
                      onChange={(e) => setRiskScoreAdjustment(e.target.value)}
                      onKeyDown={(e) => {
                        if (
                          e.key === 'Enter' &&
                          hasEditPermission &&
                          hasChanges
                        ) {
                          setShowUpdateModal(true);
                        }
                      }}
                    />
                    <Select
                      label={getLabel('sourceOfFundsVerificationStatus')}
                      disabled={!hasEditPermission || loading}
                      options={Object.keys(VerificationStatus).map((s) => ({
                        label: s,
                        value: s,
                      }))}
                      values={
                        sourceOfFundsVerificationStatus
                          ? [sourceOfFundsVerificationStatus]
                          : undefined
                      }
                      onSelect={(s) =>
                        setSourceOfFundsVerificationStatus(
                          s[0] as VerificationStatus
                        )
                      }
                      onKeyDown={(e) => {
                        if (
                          e.key === 'Enter' &&
                          hasEditPermission &&
                          hasChanges
                        ) {
                          setShowUpdateModal(true);
                        }
                      }}
                    />
                  </>
                )}
                {user && (
                  <div
                    css={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: palette.grey.default,
                      },
                    }}
                    key={'interactions'}
                  >
                    <Input
                      type={'text'}
                      label={'Interactions'}
                      value={user.interactions.join(', ')}
                      onChange={() => ({})}
                      disabled
                    />
                  </div>
                )}
                {user &&
                  Object.entries({
                    ...user,
                    ...user.kyc,
                    identities: user.kyc.identities
                      .map((identity) => {
                        return identity.eidProviderType;
                      })
                      .join(', '),
                  })
                    .filter(
                      ([k]) =>
                        ![
                          'status',
                          'riskScoreAdjustment',
                          'sourceOfFundsVerificationStatus',
                          'intergiroKyc',
                          'addressStreetLine2',
                          'kyc',
                          'onboarding',
                          'interactions',
                          'id',
                        ].includes(k)
                    )
                    .sort()
                    .map(([k, v]) => {
                      return (
                        <div
                          css={{
                            '& .MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: palette.grey.default,
                            },
                          }}
                          key={k}
                        >
                          <Input
                            type={
                              v && typeof v === 'string' && v.length > 100
                                ? 'textarea'
                                : 'text'
                            }
                            label={getLabel(k)}
                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            value={(v as string) ?? '-'}
                            onChange={() => ({})}
                            disabled
                          />
                        </div>
                      );
                    })}
              </div>
            </Card>
            {hasOnboardingHistory && (
              <Card>
                <OnboardingTable onboarding={user.kyc.onboarding.onboarding} />
              </Card>
            )}

            {user && hasEditPermission && (
              <RequestProofOfFunds
                userReference={user.externalReference}
                requested={
                  user.kyc.sourceOfFundsVerificationStatus ===
                  VerificationStatus.REQUESTED
                }
                onUpdated={search}
              />
            )}
            <FCUCards customerId={userId} customerType="APP" />

            <div
              css={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: spacing.xxs,
              }}
            >
              <UtilityButton
                css={{ marginLeft: spacing.xxs }}
                text="Customer info"
                onClick={() => {
                  setShowCustomerInfoModal(true);
                }}
              />
              {user && (
                <OffboardCustomer
                  userId={user.userId}
                  canEdit={hasEditPermission}
                  onOffboarded={() => void search()}
                />
              )}
              {hasOnboardingHistory && (
                <RetriggerOnboarding userReference={user.externalReference} />
              )}
            </div>
            {user && (
              <KycDocumentImages
                userId={user.userId}
                getKycDocumentImage={getKycDocumentImage}
                getKycDocumentImages={getKycDocumentImages}
              />
            )}
          </div>
        )}
        {tab === 'transactions' && user && (
          <Transactions key={user.userId} userId={user.userId} />
        )}
        {tab === 'history' && user && user.externalReference && (
          <History
            key={user.externalReference}
            userReference={user.externalReference}
          />
        )}
      </div>

      {showUpdateModal && (
        <Modal
          title="Are you sure you want to update this user?"
          closeModal={() => !loading && setShowUpdateModal(false)}
          primaryButtonProps={{
            onClick: () => {
              void doUpdate();
            },
            text: 'Update',
            loading: loading,
          }}
          secondaryButtonProps={{
            onClick: () => setShowUpdateModal(false),
            text: 'Cancel',
            disabled: loading,
          }}
        >
          <KeyDownAutoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') void doUpdate();
            }}
          />
        </Modal>
      )}
      {showCustomerInfoModal && user && (
        <Modal
          title="Customer information"
          subtitle="Easy way for FCU to copy important customer info"
          closeModal={() => setShowCustomerInfoModal(false)}
        >
          <div>
            <Typography>
              Business relationship established:{' '}
              {new Date(user.createdAt).toLocaleDateString('sv-se')}
            </Typography>
            <Typography>
              Full name: {user.firstName} {user.lastName}
            </Typography>
            <Typography>Email: {user.emailAddress}</Typography>
            <Typography>Phone number: {user.phoneNumber}</Typography>
            <Typography>Customer ID: {user.userId}</Typography>
            <Typography>
              Social security number / date of birth: {user.personalIdNumber}
            </Typography>
            <Typography>
              Address: {user.addressStreetLine1}, {user.addressPostalCode}{' '}
              {user.addressCity}, {user.addressCountryCode}
            </Typography>
          </div>
        </Modal>
      )}
      {showCreateCaseModal && (
        <CreateCaseModal
          customerId={userId}
          customerType="APP"
          onClose={() => setShowCreateCaseModal(false)}
        />
      )}
    </>
  );
};
